import {
  DatasetSource,
  GeoCoordinates,
  SavedPairCriteria,
  StudiesSearch,
  schGeoListingsFilters,
} from '@aloft/toolkit-shared';

import { useGetListingsByPolygon } from '../../../hooks/api/toolkitComponents.ts';
import { runMatchSearch } from '../matching/runMatchSearch.ts';
import { AdjustPairedSalesStandaloneModuleProps } from '../types.ts';
import { getListingsByPolygon } from '../utils/getListingsByPolygon.ts';
import { listingsToToolkitProperties } from '../utils/listingsToToolkitProperties.ts';
import { makePairsFromResult } from '../utils/makePairsFromResult.ts';
import { savedCriteriaToWorkerInput } from '../utils/savedCriteriaToWorkerInput.ts';

type GetPropertiesAndPairsParams = {
  coordinates: GeoCoordinates;
  search: StudiesSearch;
  pairCriteria: SavedPairCriteria;
};

export const useGetPropertiesAndPairs = ({
  getDisplayValue,
}: {
  getDisplayValue: AdjustPairedSalesStandaloneModuleProps['getDisplayValue'];
}) => {
  const getListingsByPolygonReq = useGetListingsByPolygon();

  const getPropertiesAndPairs = async ({
    coordinates,
    search,
    pairCriteria,
  }: GetPropertiesAndPairsParams) => {
    // TODO: Don't fetch listings if filters haven't changed
    const listings = await getListingsByPolygon({
      getListings: getListingsByPolygonReq,
      body: {
        coordinates: coordinates.map(([longitude, latitude]) => ({
          latitude,
          longitude,
        })),
        filters: schGeoListingsFilters.parse(search.filters),
      },
    });

    const properties = listingsToToolkitProperties({
      listings: listings.results,
      source: listings.source as DatasetSource,
    });

    const matchSearchInput = savedCriteriaToWorkerInput(pairCriteria);

    const matchSearch = runMatchSearch({
      dataset: properties,
      difference: matchSearchInput.differenceCriteria,
      match: matchSearchInput.matchCriteria,
      getPartitionKey: getDisplayValue,
      allowAnyMls: listings.source === 'abode',
    });

    const pairs = makePairsFromResult(matchSearch);

    return { properties, pairs };
  };

  return { getPropertiesAndPairs };
};
